import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Edit, Trash2, MapPin, Heart, Activity, AlertTriangle, MoreVertical } from 'lucide-react'
import { MainLayout } from '@/components/layout'
import { Button, Card } from '@/components/common'
import { useGetCowByIdQuery, useDeleteCowMutation, useGetHeatAlertsByCowQuery } from '@/store'
import { COW_BREEDS, CATTLE_TYPES } from '@/constants'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { toast } from 'react-toastify'
import { useState } from 'react'
import { calculateAge } from '@/utils'

export default function CowDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: cow, isLoading, error } = useGetCowByIdQuery(id!)
  const [deleteCow, { isLoading: isDeleting }] = useDeleteCowMutation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  // Fetch heat alerts for this cow
  const { data: heatAlertsData } = useGetHeatAlertsByCowQuery(id!)
  const activeHeatAlerts = heatAlertsData?.data?.filter(a => a.status === 'ACTIVE') || []


  const handleDelete = async () => {
    if (!window.confirm('คุณแน่ใจหรือไม่ว่าต้องการลบโคนี้?')) {
      return
    }

    try {
      await deleteCow(id!).unwrap()
      toast.success('ลบโคสำเร็จ')
      navigate('/cows')
    } catch (error: any) {
      console.error('Failed to delete cow:', error)
      toast.error(error?.data?.message || 'ลบโคไม่สำเร็จ')
    }
  }

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-800"></div>
        </div>
      </MainLayout>
    )
  }

  if (error || !cow) {
    return (
      <MainLayout>
        <div className="bg-error-light border border-error rounded-lg p-6 text-center">
          <p className="text-error">ไม่พบข้อมูลโค</p>
          <Button variant="primary" onClick={() => navigate('/cows')} className="mt-4">
            กลับไปหน้ารายการ
          </Button>
        </div>
      </MainLayout>
    )
  }

  // Calculate age
  const { years: ageYears, months: ageMonthsRemainder } = calculateAge(cow.birthDate)

  const healthStatusColors: Record<string, string> = {
    HEALTHY: 'text-success bg-success-light',
    SICK: 'text-error bg-error-light',
    TREATMENT: 'text-warning bg-warning-light',
    RECOVERED: 'text-info bg-info-light',
  }

  const healthStatusLabels: Record<string, string> = {
    HEALTHY: 'สุขภาพดี',
    SICK: 'ป่วย',
    TREATMENT: 'กำลังรักษา',
    RECOVERED: 'หายแล้ว',
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2 lg:gap-4">
            <Button
              variant="secondary"
              onClick={() => navigate('/cows')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">กลับ</span>
            </Button>
            <div>
              <h1 className="text-xl lg:text-3xl font-bold text-text-primary">
                {cow.name || cow.cowCode}
              </h1>
              {cow.name && (
                <p className="text-text-tertiary mt-1 text-xs lg:text-sm">รหัส: {cow.cowCode}</p>
              )}
            </div>
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3">
            {cow.sensorId && (
              <Button
                variant="primary"
                onClick={() => navigate(`/cows/${id}/behavior`)}
                className="flex items-center gap-2"
              >
                <Activity className="w-5 h-5" />
                ดูกราฟพฤติกรรม
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => navigate(`/cows/${id}/edit`)}
              className="flex items-center gap-2"
            >
              <Edit className="w-5 h-5" />
              แก้ไข
            </Button>
            <Button
              variant="secondary"
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex items-center gap-2 text-error hover:bg-error-light"
            >
              <Trash2 className="w-5 h-5" />
              ลบ
            </Button>
          </div>

          {/* Mobile/Tablet Menu */}
          <div className="relative lg:hidden">
            <Button
              variant="secondary"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="flex items-center gap-2"
            >
              <MoreVertical className="w-5 h-5" />
            </Button>

            {/* Dropdown Menu */}
            {isMenuOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsMenuOpen(false)}
                />

                {/* Menu Items */}
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-border-light z-20 overflow-hidden">
                  {cow.sensorId && (
                    <button
                      onClick={() => {
                        setIsMenuOpen(false)
                        navigate(`/cows/${id}/behavior`)
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-text-primary hover:bg-background-tertiary transition-colors"
                    >
                      <Activity className="w-5 h-5 text-primary-800" />
                      <span>ดูกราฟพฤติกรรม</span>
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setIsMenuOpen(false)
                      navigate(`/cows/${id}/edit`)
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-text-primary hover:bg-background-tertiary transition-colors border-t border-border-light"
                  >
                    <Edit className="w-5 h-5 text-text-secondary" />
                    <span>แก้ไขข้อมูล</span>
                  </button>
                  <button
                    onClick={() => {
                      setIsMenuOpen(false)
                      handleDelete()
                    }}
                    disabled={isDeleting}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-error hover:bg-error-light transition-colors border-t border-border-light disabled:opacity-50"
                  >
                    <Trash2 className="w-5 h-5" />
                    <span>{isDeleting ? 'กำลังลบ...' : 'ลบ'}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Heat Alert Banner */}
        {activeHeatAlerts.length > 0 && (
          <div className="bg-error-light border-l-4 border-error rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-error flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-error mb-2">
                  ⚠️ โคเคลื่อนไหวมากกว่าปกติ
                </h3>
                {activeHeatAlerts.map((alert) => (
                  <div key={alert.id} className="mb-3">
                    <p className="text-text-primary mb-1">
                      <strong>Activity Index:</strong> {alert.activityIndex.toFixed(2)}
                      {' '}(เกินค่ามาตรฐาน {alert.cutoff5.toFixed(2)})
                    </p>
                    <p className="text-sm text-text-secondary">
                      ตรวจพบเมื่อ: {format(new Date(alert.detectedAt), 'dd MMMM yyyy HH:mm น.', { locale: th })}
                    </p>
                  </div>
                ))}
                <p className="text-sm text-text-tertiary mt-2">
                  💡 <strong>แนะนำ:</strong> โปรดตรวจเช็คโค
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Image & Basic Info */}
          <div className="lg:col-span-1 space-y-6">
            {/* Image */}
            <Card>
              <div className="w-full h-[300px] bg-background-gray rounded-md overflow-hidden">
                {cow.images && cow.images.length > 0 ? (
                  <img
                    src={cow.images[0].url}
                    alt={cow.name || cow.cowCode}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-text-tertiary">
                    <Heart className="w-20 h-20" />
                  </div>
                )}
              </div>
            </Card>

            {/* Quick Stats */}
            <Card>
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                ข้อมูลพื้นฐาน
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">เพศ</span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      cow.gender === 'MALE'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-pink-100 text-pink-800'
                    }`}
                  >
                    {cow.gender === 'MALE' ? 'ผู้' : 'เมีย'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">พันธุ์</span>
                  <span className="text-text-primary font-medium">
                    {COW_BREEDS[cow.breed]}
                  </span>
                </div>

                {cow.cattleType && (
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary">กลุ่มวัว</span>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-primary-100 text-primary-800">
                      {CATTLE_TYPES[cow.cattleType]}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">สถานะสุขภาพ</span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      healthStatusColors[cow.healthStatus]
                    }`}
                  >
                    {healthStatusLabels[cow.healthStatus]}
                  </span>
                </div>

                {cow.status && (
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary">สถานะ</span>
                    <span className="text-text-primary font-medium">
                      {cow.status === 'ACTIVE' ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                    </span>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Right Column - Detailed Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* General Information */}
            <Card>
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                ข้อมูลทั่วไป
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {cow.cattleType && (
                  <div>
                    <label className="text-sm text-text-tertiary">กลุ่มวัว</label>
                    <p className="text-text-primary font-medium mt-1">
                      <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-primary-100 text-primary-800">
                        {CATTLE_TYPES[cow.cattleType]}
                      </span>
                    </p>
                  </div>
                )}

                <div>
                  <label className="text-sm text-text-tertiary">วันเกิด</label>
                  <p className="text-text-primary font-medium mt-1">
                    {format(new Date(cow.birthDate), 'dd MMMM yyyy', { locale: th })}
                  </p>
                </div>

                <div>
                  <label className="text-sm text-text-tertiary">อายุ</label>
                  <p className="text-text-primary font-medium mt-1">
                    {ageYears > 0 && `${ageYears} ปี `}
                    {ageMonthsRemainder} เดือน
                  </p>
                </div>

                {cow.weight && (
                  <div>
                    <label className="text-sm text-text-tertiary">น้ำหนัก</label>
                    <p className="text-text-primary font-medium mt-1">
                      {cow.weight} กก.
                    </p>
                  </div>
                )}

                {cow.color && (
                  <div>
                    <label className="text-sm text-text-tertiary">สี</label>
                    <p className="text-text-primary font-medium mt-1">{cow.color}</p>
                  </div>
                )}

                {cow.farmName && (
                  <div className="col-span-2">
                    <label className="text-sm text-text-tertiary">ฟาร์ม</label>
                    <p className="text-text-primary font-medium mt-1 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {cow.farmName}
                    </p>
                  </div>
                )}
              </div>
            </Card>

            {/* Purchase Information */}
            {(cow.purchaseDate || cow.purchasePrice || cow.currentValue) && (
              <Card>
                <h3 className="text-lg font-semibold text-text-primary mb-4">
                  ข้อมูลการซื้อ
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {cow.purchaseDate && (
                    <div>
                      <label className="text-sm text-text-tertiary">วันที่ซื้อ</label>
                      <p className="text-text-primary font-medium mt-1">
                        {format(new Date(cow.purchaseDate), 'dd MMMM yyyy', { locale: th })}
                      </p>
                    </div>
                  )}

                  {cow.purchasePrice && (
                    <div>
                      <label className="text-sm text-text-tertiary">ราคาซื้อ</label>
                      <p className="text-text-primary font-medium mt-1">
                        {cow.purchasePrice.toLocaleString('th-TH')} ฿
                      </p>
                    </div>
                  )}

                  {cow.currentValue && (
                    <div>
                      <label className="text-sm text-text-tertiary">มูลค่าปัจจุบัน</label>
                      <p className="text-primary-800 font-semibold text-lg mt-1">
                        {cow.currentValue.toLocaleString('th-TH')} ฿
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Notes */}
            {cow.notes && (
              <Card>
                <h3 className="text-lg font-semibold text-text-primary mb-4">
                  หมายเหตุ
                </h3>
                <p className="text-text-secondary">{cow.notes}</p>
              </Card>
            )}

            {/* Parent Information */}
            {(cow.motherId || cow.fatherId) && (
              <Card>
                <h3 className="text-lg font-semibold text-text-primary mb-4">
                  ข้อมูลพ่อแม่พันธุ์
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {cow.motherId && (
                    <div>
                      <label className="text-sm text-text-tertiary">แม่พันธุ์</label>
                      <p className="text-text-primary font-medium mt-1">
                        {cow.motherId}
                      </p>
                    </div>
                  )}

                  {cow.fatherId && (
                    <div>
                      <label className="text-sm text-text-tertiary">พ่อพันธุ์</label>
                      <p className="text-text-primary font-medium mt-1">
                        {cow.fatherId}
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  )
}
